import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';

import { UserData } from 'clipsal-cortex-types/src/api/api-user';
import { CachedSession, getCachedSession } from 'clipsal-cortex-utils/src/auth/cached-session';
import { parseJwt } from 'clipsal-cortex-utils/src/cryptography/oauth';

import { RootState } from '../../app/store';
import { get, getCurrentSession } from '../../common/api/api-helpers';
import { resolveTrackingConsent } from '../../utils/common/tracking-privacy';
import {
  clearDynatraceAuthenticationJourney,
  trackDynatraceHeliosAuthorizationResult,
  trackDynatraceTrackingEnabledContext,
} from '../../utils/native/dynatrace-events';
import { getEimIdToken } from '../auth/auth-helpers';

export interface User {
  isLoggedIn: boolean;
  token: string;
  role: string;
  accessToken: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  cognitoUser: CognitoUser | null;
  sendEmailAction: string;
  userID: number;
  tenantID: number;
  locale: string;
}

export const checkUserLoggedIn = createAsyncThunk('user/checkUserLoggedIn', async () => {
  let session: CachedSession | null;
  let isLoggedIn = false;
  let idToken = '';
  let accessToken = '';
  let cognitoUser: CognitoUser | null = null;

  try {
    session = await getCurrentSession();
    cognitoUser = await Auth.currentUserPoolUser();
    isLoggedIn = true;
    idToken = session.token;
    accessToken = session.accessToken;
  } catch (error) {
    console.error(error);
  }

  // Return the successfully authorized user's token.
  return {
    ...initialState,
    cognitoUser,
    isLoggedIn,
    accessToken,
    token: idToken,
  };
});

export const fetchUserDetails = createAsyncThunk<
  User,
  { isProduction: boolean; isNotDevelopment: boolean; version: string },
  { state: RootState }
>('user/fetchUserDetails', async ({ isNotDevelopment }, { getState }) => {
  let userData: UserData | null = null;

  try {
    [userData] = await Promise.all([get<UserData>('/v1/user')]);
  } catch (error) {
    throw new Error((error as Error).message);
  }

  const storeUser = getState().user;

  let email = userData?.email;
  // Store IdP-only property updates to local storage so they persist without the user having to log out and back in
  // TODO: investigate if we can just sync these properties to our DB instead
  let firstName = localStorage.getItem(`${userData?.user_id ?? storeUser.userID}_firstNameEIM`) ?? userData?.first_name;
  let lastName = localStorage.getItem(`${userData?.user_id ?? storeUser.userID}_lastNameEIM`) ?? userData?.last_name;

  // Only send events to sentry in staging and production environments
  if (isNotDevelopment && userData?.user_id) {
    Sentry.setUser({ id: userData.user_id.toString() });
  }

  // Get these properties from EIM ID token if they're not saved in the store already
  if (!email || !firstName || !lastName) {
    try {
      const { idToken } = await getEimIdToken();
      const parsedIdToken = parseJwt(idToken);
      email = parsedIdToken.email;
      firstName = parsedIdToken.given_name;
      lastName = parsedIdToken.family_name;
    } catch (e) {
      console.error('Failed to fetch user details from EIM, falling back to RDS user details');
    }
  }

  // Set up tracking for the user, only when logged in
  if (userData?.user_id && userData.role !== 'SUPER_ADMIN') {
    const userId = userData.user_id;
    const userRole = userData.role;
    // In addition to the `resolveTrackingConsent` call upon bootstrap, we need this call to enable GA and link it with the user ID.
    await resolveTrackingConsent(
      undefined,
      userId,
      undefined,
      false,
      userRole,
      () => {
        const siteId = getState().site.site_id;
        trackDynatraceTrackingEnabledContext({
          userId,
          userRole,
          siteId: siteId > 0 ? siteId : undefined,
        });
        trackDynatraceHeliosAuthorizationResult('success', {
          requestMethod: 'GET',
          apiOperation: 'user_profile',
        });
      },
      clearDynatraceAuthenticationJourney
    );
  }

  return {
    ...storeUser,
    email,
    firstName,
    lastName,
    phone: userData?.phone,
    sendBillsAction: userData?.send_bills_action,
    userID: userData?.user_id,
    role: userData?.role,
    tenantID: userData?.tenant_id,
  };
});

export const initialState: User = {
  isLoggedIn: false,
  cognitoUser: null,
  token: '',
  accessToken: '',
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  phone: '',
  sendEmailAction: '',
  userID: 0,
  tenantID: 0,
  locale: '',
};

export const fetchCachedSession = createAsyncThunk<User>('user/fetchCachedSession', async () => {
  const session = await getCachedSession();
  const isLoggedIn = session ? session.expiration > Date.now() / 1000 : false;
  return { ...initialState, isLoggedIn } as User;
});

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    logIn: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        isLoggedIn: true,
        token: action.payload,
      };
    },
    logOut: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkUserLoggedIn.fulfilled, (state, action) => {
        return {
          ...state,
          isLoggedIn: action.payload.isLoggedIn,
          token: action.payload.token,
          cognitoUser: action.payload.cognitoUser,
          accessToken: action.payload.accessToken,
        };
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        return action.payload ? action.payload : state;
      })
      .addCase(fetchUserDetails.rejected, () => {
        fetchCachedSession();
      });
  },
});

export const { logIn, logOut } = userSlice.actions;

export const selectUser = (state: RootState): User => {
  return state.user;
};

export const { reducer: userReducer } = userSlice;
